<template>
  <modal
    elem-id="stock-adjust"
    :show="isVisible"
    :title="t('Ajustar Estoque')"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Salvando') : t('Salvar')"
    save-test-id="stock-adjust-save-btn"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible && currentProductId">
      <SOStockAdjustForm
        ref="formRef"
        :productId="currentProductId"
        :franchiseId="currentFranchiseId"
        @submit-success="handleFormSuccess"
        @submit-error="handleFormError"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOStockAdjustForm from './SOStockAdjustForm.vue';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  loadCurrentStock: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  productId?: Id;
  franchiseId?: Id;
}

// Get i18n composable
const { t } = useI18n();

// Reactive data
const isVisible = ref<boolean>(false);
const currentProductId = ref<Id | null>(null);
const currentFranchiseId = ref<Id | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);

// Methods
const openModal = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOStockAdjustModal', 'openModal', 'Opening modal', data);
  currentProductId.value = data?.productId ?? null;
  currentFranchiseId.value = data?.franchiseId ?? null;
  isVisible.value = true;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadCurrentStock();
  }
};

const handleClose = (): void => {
  log('SOStockAdjustModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  currentProductId.value = null;
  currentFranchiseId.value = null;
  isSaving.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOStockAdjustModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleFormSuccess = (): void => {
  log('SOStockAdjustModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-stock.adjusted');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOStockAdjustModal', 'handleFormError', 'Form submission error', error);
};

// Lifecycle - Event listeners
onMounted(() => {
  log('SOStockAdjustModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.so-stock-adjust.open', openModal);
  eventBus.on('modal.so-stock-adjust.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOStockAdjustModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.so-stock-adjust.open', openModal);
  eventBus.off('modal.so-stock-adjust.close', handleClose);
});

// Expose methods for parent components
defineExpose({
  openModal
});
</script>

<style scoped>
</style>
